import {
  ApplicationConfig,
  ApplicationConfigWithPages,
  AppCategory,
  AppMappings,
  WebAppCategory,
  WebAppMapping,
} from './appConfig.models';
import { deepCopyObj } from '../utils/utils';
import { AppConfigActions, AppConfigActionTypes } from '../app/appConfig.actions';
import { createEntityAdapter, Update } from '@ngrx/entity';
import { PageActions, PageActionTypes } from 'shared/features/ng-redux/page/page.actions';
import { Shifts } from '../../../../modules/battery-pool/models/models';
import { ChartActions } from 'shared//features/ng-redux/chart/chart.actions';

export const appAdapter = createEntityAdapter<ApplicationConfig>({
  selectId: app => app.id,
});

export enum AppMode {
  DEVICES,
  BATTERIES,
  WATCHLIST,
  UTILIZATION,
}

export const initialAppState: AppConfigState = {
  entities: {},
  currentAppConfigId: null,
  ids: [],
  error: null,
  appMode: AppMode.DEVICES,
  mapsLoaded: undefined,
  lastPageAppMode: {},
  shifts: [],
  appCategories: [],
  appMappings: [],
  webAppCategories: [],
  webAppMappings: [],
};

export interface AppConfigState {
  entities: AppConfigEntities;
  currentAppConfigId: string;
  ids: string[];
  hydrated?: boolean;
  error: AppConfigError;
  appMode: AppMode;
  mapsLoaded: boolean;
  shifts: Shifts[];
  appCategories: AppCategory[];
  appMappings: AppMappings[];
  webAppCategories: WebAppCategory[];
  webAppMappings: WebAppMapping[];
  lastPageAppMode: { [key: number]: string };
}

export interface AppConfigError {
  code: AppConfigErrorCode;
  message: string;
}

export enum AppConfigErrorCode {
  UNAUTHORIZED = 'Unauthorized',
  FAILED = 'Failed',
}

export interface AppConfigEntities {
  [id: string]: ApplicationConfig;
}

export function AppConfigReducer(
  state: AppConfigState = initialAppState,
  action: AppConfigActions | PageActions | ChartActions
): AppConfigState {
  if (!window.reducerTimingDisabled) window.addTiming && window.addTiming('appConfig.reducer:' + action.type);

  switch (action.type) {
    case PageActionTypes.FETCH_PAGE_DATA_ON_PAGE_CHANGED_SUCCESS: {
      return { ...state, lastPageAppMode: { ...state.lastPageAppMode, [state.appMode]: action.pageId } };
    }

    case AppConfigActionTypes.GET_APPLICATIONS_FAILURE: {
      console.error(action.error);
      let getConfigFailure: AppConfigError;

      // Ignore unauthenticated errors because they will be be handled by http.token.interceptor
      if (action.error.name !== 'HttpErrorResponse' || action.error.status !== 401) {
        if (action.error.name === 'HttpErrorResponse' && action.error.status === 403) {
          // User is not authorized to access Insight
          getConfigFailure = { code: AppConfigErrorCode.UNAUTHORIZED, message: action.error.message };
        } else {
          // Other failure
          getConfigFailure = { code: AppConfigErrorCode.FAILED, message: action.error.message };
        }
      }

      return { ...state, error: getConfigFailure };
    }

    case AppConfigActionTypes.ADD_APP_CONFIG_SUCCESS: {
      const { pages: removed, ...appConfigWithoutPages } = action.appConfig;
      const addAppConfigPayload: ApplicationConfig = { ...appConfigWithoutPages, isSelected: false };
      const ids = action.addFirst ? [addAppConfigPayload.id, ...state.ids] : [...state.ids, addAppConfigPayload.id];
      return {
        ...state,
        entities: Object.assign({}, state.entities, {
          [addAppConfigPayload.id]: addAppConfigPayload,
        }),
        currentAppConfigId: state.currentAppConfigId,
        ids: ids,
        hydrated: state.hydrated,
        error: null,
        appMode: state.appMode,
      };
    }

    case AppConfigActionTypes.UPDATE_APP_CONFIG_SUCCESS: {
      const updateAppConfigPayload: ApplicationConfig = { ...action.appConfig };
      return {
        ...state,
        entities: Object.assign({}, state.entities, {
          [updateAppConfigPayload.id]: updateAppConfigPayload,
        }),
      };
    }

    case AppConfigActionTypes.DELETE_APP_CONFIG_SUCCESS: {
      const deleteAppConfigPayload: string = action.id;
      const indexToDelete = state.ids.indexOf(deleteAppConfigPayload);
      const entitiesCopy = Object.assign({}, state.entities);
      delete entitiesCopy[deleteAppConfigPayload];
      const currentAppConfigId = deleteAppConfigPayload === state.currentAppConfigId ? null : state.currentAppConfigId;
      return {
        ...state,
        entities: Object.assign({}, entitiesCopy),
        currentAppConfigId: currentAppConfigId,
        ids: [...state.ids.slice(0, indexToDelete), ...state.ids.slice(indexToDelete + 1, state.ids.length)],
        error: null,
      };
    }

    case PageActionTypes.HYDRATE_PAGE_SUCCESS: {
      return { ...state, hydrated: true };
    }

    case PageActionTypes.HYDRATE_PAGE_FAILURE: {
      return { ...state, hydrated: false };
    }

    case AppConfigActionTypes.SELECT_APP_CONFIG: {
      const selectAppConfigPayload: string = action.id;
      const existingStateEntities: AppConfigEntities = deepCopyObj(state.entities);
      if (!existingStateEntities[selectAppConfigPayload]) {
        return state;
      }
      if (
        selectAppConfigPayload === state.currentAppConfigId &&
        existingStateEntities[selectAppConfigPayload].isSelected
      ) {
        return state;
      }

      Object.keys(existingStateEntities).forEach(appId => {
        existingStateEntities[appId].isSelected = false;
      });
      existingStateEntities[selectAppConfigPayload].isSelected = true;

      return {
        ...state,
        entities: existingStateEntities,
        currentAppConfigId: !state.entities[selectAppConfigPayload] ? state.currentAppConfigId : selectAppConfigPayload,
        error: null,
        appMode:
          !state.entities[selectAppConfigPayload] || action.appMode === undefined ? state.appMode : action.appMode,
      };
    }

    case AppConfigActionTypes.GET_APPLICATIONS_SUCCESS: {
      const applicationsPayload: ApplicationConfigWithPages[] = action.applications;
      const entitiesCopy = {};
      applicationsPayload.forEach(appWithPages => {
        const { pages: removed, ...app } = appWithPages;
        entitiesCopy[app.id] = deepCopyObj(app);
      });

      const appIds: string[] = Object.keys(entitiesCopy);
      const existingStateEntities: AppConfigEntities = deepCopyObj(state.entities);
      const existingStateIds: string[] = deepCopyObj(state.ids);
      return {
        ...state,
        entities: Object.assign({}, existingStateEntities, entitiesCopy),
        ids: [...existingStateIds, ...appIds],
        error: null,
      };
    }

    case AppConfigActionTypes.SELECT_CHILD_TAB: {
      const app = state.entities[action.appId];
      const appUpdate: Update<any> = { id: app.id, changes: { selectedTabId: action.tabId } };
      return appAdapter.updateOne(appUpdate, state);
    }

    case AppConfigActionTypes.FETCH_SHIFT_SUCCESS: {
      return { ...state, shifts: action.shifts };
    }

    case AppConfigActionTypes.DELETE_SHIFT_SUCCESS: {
      return { ...state, shifts: state.shifts.filter(s => s.shiftId !== action.id) };
    }

    case AppConfigActionTypes.ADD_SHIFT_SUCCESS: {
      return { ...state, shifts: [...state.shifts, { ...action.shift, shiftId: action.id }] };
    }

    case AppConfigActionTypes.UPDATE_SHIFT_SUCCESS: {
      const index = state.shifts.findIndex(s => s.shiftId === action.shift.shiftId);
      return { ...state, shifts: [...state.shifts.slice(0, index), action.shift, ...state.shifts.slice(index + 1)] };
    }

    case AppConfigActionTypes.MAPS_LOADED: {
      return { ...state, mapsLoaded: action.success };
    }

    case AppConfigActionTypes.FETCH_APP_CATEGORIES_SUCCESS:
    case AppConfigActionTypes.EDIT_APP_CATEGORY:
    case AppConfigActionTypes.UPDATE_APP_CATEGORIES_SUCCESS: {
      return { ...state, appCategories: action.appCategories };
    }
    case AppConfigActionTypes.FETCH_WEB_APP_CATEGORIES_SUCCESS:
    case AppConfigActionTypes.EDIT_WEB_APP_CATEGORY:
    case AppConfigActionTypes.UPDATE_WEB_APP_CATEGORIES_SUCCESS: {
      return { ...state, webAppCategories: action.appCategories };
    }

    case AppConfigActionTypes.FETCH_APP_MAPPINGS_SUCCESS:
    case AppConfigActionTypes.EDIT_APP_MAPPING:
    case AppConfigActionTypes.UPDATE_APP_MAPPINGS_SUCCESS: {
      return { ...state, appMappings: action.mappings };
    }
    case AppConfigActionTypes.FETCH_WEB_APP_MAPPINGS_SUCCESS:
    case AppConfigActionTypes.EDIT_WEB_APP_MAPPING:
    case AppConfigActionTypes.UPDATE_WEB_APP_MAPPINGS_SUCCESS: {
      return { ...state, webAppMappings: action.mappings };
    }

    default: {
      return state;
    }
  }
}
